// A Transaction Wizard (obj_type "U") on the Run view's menu.
//
// Every launch path fills the wizard's declared inputs (design §4, §5.2), and
// on this surface they all arrive through the URL:
//   - a deep link / QR code: `/run/<tenant>/<project>/<wizard>?po_no=4521` —
//     every query value that is not the shell's own is an input;
//   - a handler='open' action button targeting the wizard: the values it
//     resolved ride the hand-off store (`?open=<id>`), as for any other target;
//   - a handler='wizard' button with presentation 'navigate': its inputs are
//     put on the query by `wizardTarget`;
//   - the Work List resuming a suspended run: `?resume=<run_id>`.
//
// When the wizard ends with `finish.then: "close"` (or is cancelled, or an
// assignee hands it back) the person LEAVES it: back to the screen they came
// from in this application, or the application's home when the wizard was
// where they arrived (a QR code, a bookmark). Never a fresh copy of the same
// wizard.

import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { openScreenPayloadOf, stashOpen } from '@twasta/page-runtime/txn'
import type { OpenActionRequest, OpenScreenPayload, WizardActionRequest } from '@twasta/page-runtime/txn'
import { PlatformWizard } from '@/livepreview/runtime/wizardHost'

/** Query keys that belong to the shell, never to the wizard's inputs. */
const RESERVED = new Set(['open', 'resume'])

/** A query string holds text; a structured value travels as JSON. */
function queryValue(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

/** The Run-view target that opens `wizard` with `inputs` (one navigation). */
export function wizardTarget(
  wizard: string, inputs?: Record<string, unknown>, resumeRunId?: string,
): { objName: string; params: Record<string, string> } {
  const params: Record<string, string> = {}
  for (const [k, v] of Object.entries(inputs || {})) {
    if (RESERVED.has(k)) continue
    const text = queryValue(v)
    if (text !== '') params[k] = text
  }
  if (resumeRunId) params.resume = resumeRunId
  return { objName: wizard, params }
}

export default function WizardPane({
  project, objName, screenState, openPayload, onOpenTarget, onScreenStateChange,
}: {
  project: string
  objName: string
  /** The query string of the open screen (the shell's own keys removed). */
  screenState?: Record<string, string>
  /** What a handler='open' action button handed over, when it targeted this. */
  openPayload?: OpenScreenPayload | null
  onOpenTarget?: (target: { objName: string; params: Record<string, string> }) => void
  onScreenStateChange?: (next: Record<string, string> | null) => void
}) {
  const queryKey = JSON.stringify(screenState || {})
  const payloadParams = openPayload?.params
  const inputs = useMemo(() => {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(screenState || {})) {
      if (!RESERVED.has(k)) out[k] = v
    }
    // An action button's resolved values win over a stale query value.
    return { ...out, ...(payloadParams || {}) }
    // queryKey stands for screenState (a fresh object on every URL change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, payloadParams])
  const resumeRunId = screenState?.resume || undefined

  const navigateObject = useCallback((req: OpenActionRequest) => {
    if (!onOpenTarget) return
    const id = stashOpen(openScreenPayloadOf(req))
    onOpenTarget({ objName: req.objName, params: { open: id } })
  }, [onOpenTarget])

  const openWizard = useCallback((req: WizardActionRequest) => {
    onOpenTarget?.(wizardTarget(req.objName, req.inputs))
  }, [onOpenTarget])

  const startWizard = useCallback((wizard: string, next: Record<string, unknown>) => {
    onOpenTarget?.(wizardTarget(wizard, next))
  }, [onOpenTarget])

  const openRecord = useCallback((target: string, txnId: string) => {
    onOpenTarget?.({ objName: target, params: { mode: 'edit', rec: txnId } })
  }, [onOpenTarget])

  // finish.then 'close' / cancel: leave. The Run view owns the URL (it is the
  // one host that hands down `onScreenStateChange`); an entry this tab made
  // in the application before the wizard is "where they came from" — the
  // router keys every entry it pushed, and only the one the tab OPENED on is
  // `default` — otherwise the application's home: this path without the
  // wizard's segment (`/run/<tenant>/<project>`, or `/` on its own domain).
  const navigate = useNavigate()
  const location = useLocation()
  const leave = useCallback(() => {
    if (location.key && location.key !== 'default') {
      navigate(-1)
      return
    }
    const segment = `/${encodeURIComponent(objName)}`
    const path = location.pathname.endsWith(segment)
      ? location.pathname.slice(0, -segment.length) || '/'
      : '/'
    navigate(path, { replace: true })
  }, [navigate, location.key, location.pathname, objName])

  return (
    <div data-tw-wizard-pane={objName} style={{ height: '100%', minHeight: 0 }}>
      <PlatformWizard
        project={project}
        wizard={objName}
        inputs={inputs}
        resumeRunId={resumeRunId}
        presentation="screen"
        onNavigateObject={onOpenTarget ? navigateObject : undefined}
        openWizard={onOpenTarget ? openWizard : undefined}
        onStartWizard={onOpenTarget ? startWizard : undefined}
        onOpenRecord={onOpenTarget ? openRecord : undefined}
        // Not URL-driven (the builder's embedded preview): nowhere to go, so
        // the host starts the wizard over.
        onClose={onScreenStateChange ? leave : undefined}
      />
    </div>
  )
}
